import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../components/Button.js';
import { OnlineDot } from '../components/Badge.js';
import { toast } from '../components/Toast.js';
import { api, type ApiError } from '../lib/api.js';
import { ws } from '../lib/ws.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface MachineListItem {
  id: string;
  name: string;
  os: string | null;
  hostname: string | null;
  online: boolean;
  lastSeenAt: number | null;
}

export function MachinesScreen() {
  const [machines, setMachines] = useState<MachineListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  useDocumentTitle(buildTitle('Машины'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<MachineListItem[]>('/api/v1/machines');
      setMachines(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const unsub = ws.onMessage((msg) => {
      if (msg.type === 'machine_online') {
        setMachines((prev) =>
          prev.map((m) => (m.id === msg.machineId ? { ...m, online: msg.online } : m)),
        );
      }
    });
    return unsub;
  }, [load]);

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      <header className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-semibold">Machines</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4" /> Add
          </Button>
        </div>
      </header>

      {loading && machines.length === 0 ? (
        <p className="text-neutral-500 text-sm">Загружаем…</p>
      ) : machines.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 p-6 text-center space-y-3">
          <p className="text-neutral-400">Нет ни одной машины</p>
          <Button onClick={() => setShowAdd(true)}>Добавить машину</Button>
        </div>
      ) : (
        <ul className="space-y-2">
          {machines.map((m) => (
            <li key={m.id} className="relative">
              <Link
                to={`/machines/${m.id}`}
                className="block rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4 pr-14 active:bg-neutral-900"
              >
                <div className="flex items-center gap-3">
                  <OnlineDot online={m.online} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {m.hostname ?? '—'} · {m.os ?? '—'}
                    </div>
                  </div>
                </div>
              </Link>
              <button
                type="button"
                aria-label={`Удалить ${m.name}`}
                className="absolute top-1/2 right-3 -translate-y-1/2 p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!confirm(`Удалить машину "${m.name}"?`)) return;
                  try {
                    await api.delete(`/api/v1/machines/${m.id}`);
                    toast.success(`Удалено: ${m.name}`);
                    await load();
                  } catch (err) {
                    toast.error(`Не удалось удалить: ${(err as ApiError).code ?? (err as Error).message}`);
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd && <AddMachineDialog onClose={() => setShowAdd(false)} onAdded={() => void load()} />}
    </div>
  );
}

function AddMachineDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ machineId: string; token: string } | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post<{ machineId: string; token: string }>('/api/v1/machines', {
        name: name.trim(),
      });
      setResult(res);
      toast.success(`Машина "${name.trim()}" создана`);
      onAdded();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('create machine failed', err);
      toast.error(
        `Не удалось создать: ${(err as ApiError).code ?? (err as Error).message ?? 'network_error'}`,
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-950 p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {!result ? (
          <form onSubmit={create} className="space-y-3">
            <h2 className="text-lg font-semibold">Новая машина</h2>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="home-pc / macbook-pro"
              className="w-full h-11 px-3 rounded-xl bg-neutral-900 border border-neutral-800"
            />
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
                Отмена
              </Button>
              <Button type="submit" disabled={loading || !name.trim()} className="flex-1">
                {loading ? 'Создаём…' : 'Создать'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Запусти на машине</h2>
            <p className="text-sm text-neutral-400">
              Токен показывается один раз. Скопируй и выполни в папке монорепо:
            </p>
            <pre className="rounded-xl bg-neutral-900 border border-neutral-800 p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
              {`pnpm --filter @claude-remote/agent exec \\
  tsx src/cli.ts init \\
  --backend ${location.origin} \\
  --machine-id ${result.machineId} \\
  --token ${result.token}

pnpm --filter @claude-remote/agent dev`}
            </pre>
            <Button onClick={onClose} className="w-full">
              Готово
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
